import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import SimpleSelect from './SimpleSelect';
import PlatformExtrasPanel, {
  EMPTY_EXTRAS, EXTRAS_PLATFORMS, PinterestFields, toPlatformOptionsPayload, validateExtras,
} from './PlatformExtrasPanel';
import TikTokOptionsPanel, {
  EMPTY_TIKTOK_OPTIONS, toTikTokPayload, validateTikTok,
} from './TikTokOptionsPanel';

export const OPTION_PLATFORMS = [...EXTRAS_PLATFORMS, 'tiktok'];
const TIKTOK_IMAGE_EXT = ['jpg', 'jpeg', 'png', 'webp'];
const urlExt = (u) => (u || '').split('?')[0].split('.').pop().toLowerCase();

/**
 * Everything platform specific for publishing (TikTok privacy/cover, IG story/
 * collab/trial, FB reel/cover, Pinterest board, YouTube title/tags …) in ONE
 * place, used by "New Post" (Distribution) and "Veröffentlichen" (Produktion).
 *
 * The parent owns text + media and passes them in; this component owns the
 * option state, loads boards / TikTok creator info, and exposes
 *   ref.validate()   -> German error message | null
 *   ref.getPayload() -> fields to merge into the create/schedule request
 */
const PostOptionsSection = forwardRef(function PostOptionsSection(
  { platform, text = '', imageUrls = [], videoUrl = '', apiBase, getAuthHeader, onVideoDuration, pin, onPinChange },
  ref,
) {
  const [extras, setExtras] = useState(EMPTY_EXTRAS);
  const [altTexts, setAltTexts] = useState([]);
  const [duration, setDuration] = useState(0);
  const [tiktok, setTiktok] = useState(EMPTY_TIKTOK_OPTIONS);
  const [tiktokInfo, setTiktokInfo] = useState(null);
  const [tiktokInfoError, setTiktokInfoError] = useState('');
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState('');
  const [boardsError, setBoardsError] = useState('');
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const kind = videoUrl ? 'video' : (imageUrls.length > 0 ? 'image' : 'none');
  const tiktokKind = videoUrl ? 'video' : (imageUrls.length > 0 ? 'photo' : 'none');

  const loadBoards = useCallback(async () => {
    setBoardsError('');
    try {
      const r = await axios.get(`${apiBase}/distribution/pinterest/boards`, { headers: authRef.current() });
      const list = r.data.boards || [];
      setBoards(list);
      setBoardId((prev) => prev || r.data.default_board_id || (list.length === 1 ? list[0].id : ''));
      if (!list.length) setBoardsError('Auf deinem Pinterest-Konto wurde keine Pinnwand gefunden. Lege zuerst eine Pinnwand in Pinterest an.');
    } catch (err) {
      setBoards([]);
      setBoardsError(err?.response?.data?.detail || 'Pinnwände konnten nicht geladen werden.');
    }
  }, [apiBase]);

  // Reset + load per platform.
  useEffect(() => {
    setExtras(EMPTY_EXTRAS);
    setAltTexts([]);
    setDuration(0);
    setTiktok(EMPTY_TIKTOK_OPTIONS);
    setTiktokInfo(null);
    setTiktokInfoError('');
    setBoardId('');
    setBoardsError('');
    if (platform === 'pinterest') loadBoards();
    if (platform === 'tiktok') {
      axios.get(`${apiBase}/distribution/tiktok/creator-info`, { headers: authRef.current() })
        .then((r) => setTiktokInfo(r.data))
        .catch((err) => setTiktokInfoError(err?.response?.data?.detail || 'TikTok-Einstellungen konnten nicht geladen werden.'));
    }
  }, [platform, apiBase, loadBoards]);

  // A new/removed video invalidates the measured duration and frame cover.
  useEffect(() => {
    setDuration(0);
    setExtras((p) => ({ ...p, thumbOffset: 0 }));
    setTiktok((p) => ({ ...p, videoCoverTimestampMs: 0 }));
  }, [videoUrl]);

  useEffect(() => { onVideoDuration?.(duration); }, [duration, onVideoDuration]);

  const uploadCover = async (file) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Das Cover muss ein JPG oder PNG sein.');
      return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Das Cover darf maximal 8 MB groß sein.');
      return null;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${apiBase}/upload/image`, fd, {
        headers: { ...authRef.current(), 'Content-Type': 'multipart/form-data' },
      });
      return r.data.url;
    } catch (err) {
      toast.error('Cover-Upload fehlgeschlagen.');
      return null;
    }
  };

  const generateAlt = async (urls) => {
    try {
      const r = await axios.post(
        `${apiBase}/distribution/alt-text`,
        { image_urls: urls, text, language: 'de' },
        { headers: authRef.current() },
      );
      if (r.data.failed) toast.warning(`${r.data.failed} Alt-Text(e) konnten nicht erzeugt werden.`);
      return r.data.alt_texts;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Alt-Texte konnten nicht erzeugt werden.');
      return null;
    }
  };

  useImperativeHandle(ref, () => ({
    validate() {
      if (platform === 'pinterest') {
        if (!boardId) return boardsError || 'Pinterest: Bitte wähle eine Pinnwand aus.';
        if (imageUrls.length === 0 && !videoUrl) return 'Pinterest: Bitte ein Bild oder Video hinzufügen.';
        if (imageUrls.length > 0 && videoUrl) return 'Pinterest: Bitte entweder ein Bild oder ein Video verwenden.';
      }
      if (EXTRAS_PLATFORMS.includes(platform)) {
        const e = validateExtras(platform, extras, kind, imageUrls.length);
        if (e) return e;
      }
      if (platform === 'tiktok') {
        if (videoUrl && imageUrls.length > 0) return 'TikTok: Bitte entweder ein Video oder Bilder (Foto-Karussell) – nicht beides.';
        if (imageUrls.some((u) => !TIKTOK_IMAGE_EXT.includes(urlExt(u)))) return 'TikTok-Fotos müssen JPG, PNG oder WebP sein.';
        return validateTikTok({
          opts: tiktok, kind: tiktokKind, text, imageCount: imageUrls.length, durationSec: duration, info: tiktokInfo,
        });
      }
      return null;
    },
    getPayload() {
      const out = {};
      if (EXTRAS_PLATFORMS.includes(platform)) {
        const po = toPlatformOptionsPayload(platform, extras, kind);
        if (Object.keys(po).length) out.platform_options = po;
        if (altTexts.some((a) => a && a.trim())) out.alt_texts = altTexts.slice(0, imageUrls.length);
      }
      if (platform === 'pinterest' && boardId) out.pinterest_board_id = boardId;
      if (platform === 'tiktok') {
        out.tiktok_options = toTikTokPayload(tiktok, tiktokKind);
        if (tiktokKind === 'video' && duration) out.video_duration_sec = duration;
      }
      return out;
    },
  }), [platform, extras, altTexts, boardId, boardsError, tiktok, tiktokInfo, duration, kind, tiktokKind, imageUrls, videoUrl, text]);

  if (!OPTION_PLATFORMS.includes(platform)) return null;

  return (
    <div className="space-y-3" data-testid="post-options-section">
      {EXTRAS_PLATFORMS.includes(platform) && (
        <PlatformExtrasPanel
          platform={platform}
          kind={kind}
          value={extras}
          onChange={setExtras}
          videoUrl={videoUrl}
          imageUrls={imageUrls}
          altTexts={altTexts}
          onAltTextsChange={setAltTexts}
          onUploadCover={uploadCover}
          onGenerateAlt={generateAlt}
          durationSec={duration}
          onDuration={setDuration}
        >
          {platform === 'pinterest' && (
            <>
              {pin && onPinChange && <PinterestFields pin={pin} onPinChange={onPinChange} />}
              <div>
                <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Pinnwand *</Label>
                <SimpleSelect
                  value={boardId}
                  onChange={setBoardId}
                  options={boards.map((b) => ({ value: b.id, label: b.name }))}
                  placeholder="Pinnwand auswählen…"
                  testId="pinterest-board-select"
                />
                {boardsError && (
                  <p className="text-[10px] text-amber-300/90 mt-1" data-testid="pinterest-boards-error">
                    {boardsError}{' '}
                    <button type="button" className="underline" onClick={loadBoards}>Erneut laden</button>
                  </p>
                )}
              </div>
            </>
          )}
        </PlatformExtrasPanel>
      )}
      {platform === 'tiktok' && (
        <TikTokOptionsPanel
          value={tiktok}
          onChange={setTiktok}
          kind={tiktokKind}
          videoUrl={videoUrl}
          imageUrls={imageUrls}
          text={text}
          info={tiktokInfo}
          infoError={tiktokInfoError}
          durationSec={duration}
          onDuration={setDuration}
        />
      )}
    </div>
  );
});

export default PostOptionsSection;
